import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Segmented } from '@/components/ui/Segmented';
import { listOwners, storageKeys, type StorageOwner } from './storage.api';

// Het filter op eigenaar (design_handoff_opslag/eigenaren.md §4, bouwlijst eigenaren stap 7):
// één keuze voor heel Opslag. Kies je Greener op het overzicht, dan opent Barneveld met
// Greener, en het dashboard ook. Hij staat in de link (`?owner=greener`, de slug van de API),
// zodat een gedeelde link hetzelfde laat zien, en deze browser onthoudt hem; de link wint.
// Met één eigenaar is er geen keuze en ziet elk scherm eruit als altijd. Exporteren volgt
// hem niet: daar kies je elke keer bewust voor wie.

const OWNER_KEY = 'vht:storage:owner';

/** `?owner=` as a route reads it: a slug, or nothing. Whether anyone has it, the hook checks. */
export const ownerSearch = (search: Record<string, unknown>): string | undefined =>
  typeof search['owner'] === 'string' && search['owner'] ? search['owner'] : undefined;

/** What this browser remembered; a convenience, so it may fail. */
function remembered(): string | undefined {
  try {
    return localStorage.getItem(OWNER_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function remember(slug: string | undefined): void {
  try {
    if (slug) localStorage.setItem(OWNER_KEY, slug);
    else localStorage.removeItem(OWNER_KEY);
  } catch {
    /* private mode or blocked storage: the link still carries it */
  }
}

export interface OwnerFilterState {
  /** Every owner, by name; empty until they are loaded. */
  owners: StorageOwner[];
  /** The owner chosen, or null for all of them (and always with one owner). */
  owner: StorageOwner | null;
  /** The owners are loaded, or failed to: until then the dashboard waits. */
  ready: boolean;
  choose: (owner: StorageOwner | null) => void;
}

/**
 * The owner chosen, from the link (`linked`) or else from this browser. Keeps the two in
 * step: a remembered choice goes into the link, a link's choice into memory, and a slug
 * nobody has (an owner that went) out of the link.
 */
export function useOwnerFilter(
  linked: string | undefined,
  onLinked: (slug: string | undefined) => void,
): OwnerFilterState {
  const query = useQuery({ queryKey: storageKeys.owners, queryFn: listOwners });
  const owners = query.data ?? [];
  const slug = linked ?? remembered();
  const owner = owners.length > 1 ? (owners.find((o) => o.slug === slug) ?? null) : null;

  const loaded = query.data !== undefined;
  const want = owner?.slug;
  useEffect(() => {
    if (!loaded) return;
    if (want) remember(want);
    if (linked !== want) onLinked(want);
  }, [loaded, want, linked, onLinked]);

  return {
    owners,
    owner,
    ready: !query.isLoading,
    choose: (next) => {
      remember(next?.slug);
      onLinked(next?.slug);
    },
  };
}

/**
 * The choice itself: _Alle · Alfen · Greener_ as a `.vht-seg`, from five owners a list.
 * Nothing with one owner. `wide` fills the side column of a plan.
 */
export function OwnerFilter({
  filter,
  wide = false,
}: {
  filter: OwnerFilterState;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  const { owners, owner, choose } = filter;
  if (owners.length < 2) return null;
  const pick = (id: string) => choose(owners.find((o) => o.id === id) ?? null);
  if (owners.length >= 5) {
    return (
      <select
        className={wide ? 'vht-select vht-opslag-owner is-wide' : 'vht-select vht-opslag-owner'}
        aria-label={t('storage.ownerFilter')}
        value={owner?.id ?? ''}
        onChange={(e) => pick(e.target.value)}
      >
        <option value="">{t('storage.ownerAll')}</option>
        {owners.map((o) => (
          <option key={o.id} value={o.id}>
            {o.shortName}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Segmented
      label={t('storage.ownerFilter')}
      className={wide ? 'vht-opslag-owner is-wide' : 'vht-opslag-owner'}
      value={owner?.id ?? ''}
      onChange={pick}
      options={[
        { value: '', label: t('storage.ownerAll') },
        ...owners.map((o) => ({ value: o.id, label: o.shortName })),
      ]}
    />
  );
}
